import { useState } from 'react';
import { api, type ArmCounters, type ExperimentReport } from '../api';
import { run, useAsync } from '../hooks';
import type { EditorProps } from '../pages/SitePage';

const pct = (x: number, d = 1) => `${(x * 100).toFixed(d)}%`;
const fmtDate = (iso: string) => new Date(iso).toLocaleDateString();

/** A/B test dashboard and controls (planning.md FR-X-05, FR-X-06, §11, D-27). */
export function ExperimentPanel({ bundle, reload, show }: EditorProps) {
  const { site, goals } = bundle;
  const { data, loading, error, reload: refetch } = useAsync(() => api.experiment(site.siteId), [site.siteId, site.publishedConfigVersion]);
  const [control, setControl] = useState<string>('none');
  const [busy, setBusy] = useState(false);

  const act = async (p: Promise<unknown>, ok: string) => {
    setBusy(true);
    const r = await run(p, show, ok);
    setBusy(false);
    if (r !== null) {
      await refetch();
      await reload();
    }
  };

  if (loading && !data) return <p className="muted">Loading…</p>;
  if (error) return <p className="badge bad">{error}</p>;
  if (!data) return null;
  const { experiment: exp, stats, arms } = data;
  const goalLabel = goals.find((g) => g.goalId === data.primaryGoalId)?.label ?? data.primaryGoalId ?? 'primary goal';

  if (!exp) {
    return (
      <div className="empty">
        No experiment yet. Publishing the first configuration starts a 50/50 test automatically.
      </div>
    );
  }

  const status = exp.status === 'productionised' ? 'Springboard on for everyone' : exp.status === 'ended' ? 'Ended' : data.expired ? 'Time is up' : 'Running';
  const statusCls = exp.status === 'productionised' ? 'accent' : exp.status === 'running' && !data.expired ? 'ok' : 'warn';
  const controlLabel = exp.controlVersion ? `Config v${exp.controlVersion}` : 'No widget';

  return (
    <div className="grid" style={{ gap: 18 }}>
      <div className="card">
        <div className="row spread">
          <div className="row">
            <h2>Experiment</h2>
            <span className={`badge ${statusCls}`}>{status}</span>
            {data.expired && exp.status === 'running' && <span className="badge warn">visitors see control until extended or paid</span>}
          </div>
          <div className="row">
            {exp.status === 'running' && (
              <>
                <button className="btn sm" disabled={busy} onClick={() => act(api.extendExperiment(site.siteId, 14), 'Extended by 14 days')}>
                  Extend 14 days
                </button>
                <button className="btn sm" disabled={busy} onClick={() => confirm('End the test? Visitors will see the control until you start a new one or switch Springboard on.') && act(api.endExperiment(site.siteId), 'Test ended')}>
                  End test
                </button>
              </>
            )}
            {exp.status !== 'productionised' && (
              <button className="btn sm primary" disabled={busy} onClick={() => confirm('Serve the Springboard to 100% of visitors? The test stops; tracking continues.') && act(api.productionise(site.siteId), 'Springboard is on for everyone')}>
                {stats?.decision === 'winner_variation' ? 'Productionise the winner' : 'Skip test, go 100%'}
              </button>
            )}
          </div>
        </div>
        <div className="grid cols-4" style={{ marginTop: 16 }}>
          <div className="stat"><div className="l">Control</div><div className="v" style={{ fontSize: 18 }}>{controlLabel}</div></div>
          <div className="stat"><div className="l">Variation</div><div className="v" style={{ fontSize: 18 }}>Current config (v{site.publishedConfigVersion ?? '—'})</div></div>
          <div className="stat"><div className="l">Split</div><div className="v">{pct(1 - exp.split, 0)} / {pct(exp.split, 0)}</div></div>
          <div className="stat"><div className="l">{exp.status === 'running' ? 'Ends' : 'Ran'}</div><div className="v" style={{ fontSize: 18 }}>{exp.status === 'running' ? `${fmtDate(exp.plannedEndAt)} (${data.daysLeft} days left)` : `${fmtDate(exp.startedAt)} → ${fmtDate(exp.endedAt ?? exp.plannedEndAt)}`}</div></div>
        </div>
        <p className="small muted" style={{ marginTop: 10 }}>
          Started {fmtDate(exp.startedAt)} · {Math.floor(data.daysRunning)} days of data · primary goal: <strong>{goalLabel}</strong>{exp.extensions ? ` · extended ${exp.extensions}×` : ''}. Visitors are assigned once by a hash of their visitor id, so each browser stays in its arm.
        </p>
      </div>

      {stats && <Decision stats={stats} goalLabel={goalLabel} />}

      <div className="grid cols-2">
        <div className="card" style={{ padding: 0 }}>
          <table>
            <thead><tr><th>Per visitor</th><th>Control</th><th>Springboard</th></tr></thead>
            <tbody>
              <tr><td>Visitors</td><td>{arms.control.visitors}</td><td>{arms.variation.visitors}</td></tr>
              <tr><td>Converted ({goalLabel})</td><td>{stats?.control.conversions ?? 0}</td><td>{stats?.variation.conversions ?? 0}</td></tr>
              <tr><td>Conversion rate</td><td>{stats ? pct(stats.control.rate, 2) : '—'}</td><td>{stats ? pct(stats.variation.rate, 2) : '—'}</td></tr>
              {goals.filter((g) => g.goalId !== data.primaryGoalId).map((g) => (
                <tr key={g.goalId} className="small"><td>Converted ({g.label})</td><td>{arms.control.convertedVisitors[g.goalId] ?? 0}</td><td>{arms.variation.convertedVisitors[g.goalId] ?? 0}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="card" style={{ padding: 0 }}>
          <table>
            <thead><tr><th>Engagement</th><th>Control</th><th>Springboard</th></tr></thead>
            <tbody>
              {(
                [
                  ['Page views', 'pageViews'],
                  ['Springboard shown', 'widgetShown'],
                  ['Questions answered', 'answers'],
                  ['Card clicks', 'cardClicks'],
                  ['Dismissed', 'dismissed'],
                  ['Conversion events (raw)', 'conversionEvents'],
                ] as [string, keyof Omit<ArmCounters, 'convertedVisitors'>][]
              ).map(([label, key]) => (
                <tr key={key}><td>{label}</td><td>{arms.control[key]}</td><td>{arms.variation[key]}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {data.daily.length > 0 && (
        <div className="card" style={{ padding: 0 }}>
          <table>
            <thead><tr><th>Day</th><th>Control visitors</th><th>Control converted</th><th>Springboard visitors</th><th>Springboard converted</th></tr></thead>
            <tbody>
              {data.daily.map((d) => (
                <tr key={d.date}>
                  <td className="mono">{d.date}</td>
                  <td>{d.control.visitors}</td><td>{d.control.convertedVisitors[data.primaryGoalId ?? ''] ?? 0}</td>
                  <td>{d.variation.visitors}</td><td>{d.variation.convertedVisitors[data.primaryGoalId ?? ''] ?? 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="card">
        <h3>Start a new test</h3>
        <p className="muted small" style={{ margin: '6px 0 12px' }}>
          Ends the current test and starts a fresh 50/50 test. The variation is always the current published configuration; choose what the control sees.
        </p>
        <div className="row">
          <select className="select" style={{ width: 260 }} value={control} onChange={(e) => setControl(e.target.value)}>
            <option value="none">Control: no widget</option>
            {data.publishedVersions.filter((v) => v !== site.publishedConfigVersion).map((v) => (
              <option key={v} value={String(v)}>Control: configuration v{v}</option>
            ))}
          </select>
          <button className="btn" disabled={busy} onClick={() => confirm('Start a new test? Current results stay on record; visitors are re-assigned.') && act(api.startExperiment(site.siteId, { controlVersion: control === 'none' ? null : Number(control) }), 'New test started')}>
            Start new test
          </button>
        </div>
      </div>
    </div>
  );
}

function Decision({ stats, goalLabel }: { stats: NonNullable<ExperimentReport['stats']>; goalLabel: string }) {
  const headline =
    stats.decision === 'winner_variation'
      ? 'Springboard wins.'
      : stats.decision === 'winner_control'
        ? 'Control wins.'
        : stats.decision === 'insufficient'
          ? 'Too early to say.'
          : 'Inconclusive so far.';
  const cls = stats.decision === 'winner_variation' ? 'ok' : stats.decision === 'winner_control' ? 'bad' : 'warn';
  return (
    <div className="card">
      <div className="row">
        <h2>{headline}</h2>
        <span className={`badge ${cls}`}>{pct(stats.probVariationBeats)} probability Springboard converts better on {goalLabel}</span>
      </div>
      <div className="grid cols-4" style={{ marginTop: 14 }}>
        <div className="stat"><div className="l">Relative lift</div><div className="v">{stats.lift ? `${stats.lift.point >= 0 ? '+' : ''}${pct(stats.lift.point, 0)}` : '—'}</div><div className="small muted">{stats.lift ? `95% interval ${pct(stats.lift.low, 0)} to ${pct(stats.lift.high, 0)}` : 'needs conversions in control'}</div></div>
        <div className="stat"><div className="l">Expected loss if you pick Springboard</div><div className="v">{pct(stats.expectedLoss.variation)}</div><div className="small muted">of conversion rate</div></div>
        <div className="stat"><div className="l">z-test p-value</div><div className="v">{stats.zTestPValue === null ? '—' : stats.zTestPValue.toFixed(3)}</div><div className="small muted">for reference</div></div>
        <div className="stat"><div className="l">Sample target</div><div className="v">{stats.sampleSizeTarget ? stats.sampleSizeTarget.perArm.toLocaleString() : '—'}</div><div className="small muted">{stats.sampleSizeTarget ? `visitors per arm to detect +${pct(stats.sampleSizeTarget.relativeLift, 0)}${stats.projectedDaysToTarget !== null ? ` · ~${stats.projectedDaysToTarget} more days at current traffic` : ''}` : 'needs a baseline'}</div></div>
      </div>
      {stats.reasons.length > 0 && (
        <ul className="small muted" style={{ marginTop: 12, paddingLeft: 18, listStyle: 'disc' }}>
          {stats.reasons.map((r) => <li key={r}>{r}</li>)}
        </ul>
      )}
      <p className="small muted" style={{ marginTop: 10 }}>Rule: a winner needs at least 95% (or at most 5%) probability, 30 converted visitors per arm and 7 days of data. Early numbers are shown but not acted on.</p>
    </div>
  );
}
